"use client";

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  useSyncLatest,
  useTriggerSync,
  useSyncStatus,
} from "@/hooks/use-analytics";
import { usePermissions } from "@/hooks/use-permissions";

interface SyncStatusHeaderProps {
  brandId: string;
}

export function SyncStatusHeader({ brandId }: SyncStatusHeaderProps) {
  const { t } = useTranslation("analytics");
  const qc = useQueryClient();
  const { canTriggerSync } = usePermissions();
  const { data: latest } = useSyncLatest(brandId);
  const [jobId, setJobId] = useState<string | null>(null);
  const { data: jobStatus } = useSyncStatus(brandId, jobId);
  const triggerSync = useTriggerSync(brandId);

  useEffect(() => {
    if (!jobStatus) return;
    if (jobStatus.status === "COMPLETED") {
      qc.invalidateQueries({ queryKey: ["analytics", brandId] });
      toast.success(t("sync.refreshSuccess"));
      setJobId(null);
    } else if (jobStatus.status === "FAILED") {
      toast.error(jobStatus.error ?? t("sync.failed"));
      setJobId(null);
    }
  }, [jobStatus, qc, brandId]);

  const handleRefresh = () => {
    triggerSync.mutate(undefined, {
      onSuccess: (data: { jobId: string }) => {
        setJobId(data.jobId);
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "";
        if (msg.includes("429") || /rate/i.test(msg)) {
          toast.error(t("sync.rateLimited"));
        } else {
          toast.error(t("sync.syncFailed"));
        }
      },
    });
  };

  const isBusy =
    triggerSync.isPending ||
    (!!jobId &&
      (jobStatus?.status === "PENDING" || jobStatus?.status === "RUNNING"));

  const lastSyncedLabel = latest?.lastSyncedAt
    ? formatDistanceToNow(new Date(latest.lastSyncedAt), { addSuffix: true })
    : "never";
  const nextSyncLabel = latest?.nextSyncAt
    ? formatDistanceToNow(new Date(latest.nextSyncAt), { addSuffix: true })
    : null;

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border bg-card p-3">
      <div className="text-sm text-muted-foreground">
        <div>
          {t("sync.lastSynced")}{" "}
          <span className="font-medium text-foreground">{lastSyncedLabel}</span>
        </div>
        {nextSyncLabel && (
          <div className="text-xs">
            {t("sync.nextSync")} {nextSyncLabel}
          </div>
        )}
      </div>
      {canTriggerSync && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isBusy}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isBusy ? "animate-spin" : ""}`}
          />
          {t("sync.refresh")}
        </Button>
      )}
    </div>
  );
}
